import React, { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Download, Copy, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";

const POSTS = [
  {
    id: "announce",
    label: "Exhibitor Announcement",
    body: (co) => `We're proud to join Travel + Leisure at the Strategic Supplier Summit. ${co} will be exhibiting alongside category leaders driving the future of hospitality sourcing. Come say hello.`,
  },
  {
    id: "meet",
    label: "Networking Invitation",
    body: (co) => `Are you attending the Travel + Leisure Strategic Supplier Summit? Stop by the ${co} station — we'd love to talk supplier readiness, innovation, and partnership.`,
  },
  {
    id: "thanks",
    label: "Post-Event Thank You",
    body: (co) => `Thank you to Travel + Leisure and Display America for hosting such a thoughtful Strategic Supplier Summit. ${co} left inspired by the conversations and ready for what's next.`,
  },
];

const HASHTAGS = ["#TLSupplierSummit", "#TravelAndLeisure", "#StrategicSourcing", "#SupplierDiversity", "#Procurement"];

export default function Toolkit() {
  const { user } = useAuth();
  const [exhibitor, setExhibitor] = useState(null);
  const [company, setCompany] = useState("Our team");

  useEffect(() => {
    if (!user) return;
    api.get("/exhibitors/me").then(({ data }) => {
      setExhibitor(data.exhibitor);
      setCompany(data.exhibitor.company_name);
    }).catch(() => {});
  }, [user]);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copied to clipboard."); };

  const Card = ({ kind, title, sub }) => (
    <div className="card-flat overflow-hidden">
      <SocialGraphic kind={kind} company={company} />
      <div className="p-5 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mt-1">{sub}</div>
        </div>
        <button onClick={() => downloadSvgAsPng(`graphic-${kind}`, `${kind}-${company.replace(/\s/g,'_')}.png`)}
          className="btn-outline-black h-10 px-4 text-xs" data-testid={`download-${kind}`}>
          <Download size={14} /> PNG
        </button>
      </div>
    </div>
  );

  return (
    <Layout>
      <section className="container-summit py-12 md:py-16">
        <div className="overline mb-4"><span className="accent-rule" />Social Media Toolkit</div>
        <h1 className="text-4xl sm:text-5xl">Announce your participation.</h1>
        <p className="mt-4 max-w-2xl text-zinc-500">
          Generate branded graphics personalised to your company and copy LinkedIn-ready captions in seconds.
        </p>

        {user && (
          <div className="mt-8 flex items-center gap-3">
            <Sparkles size={14} className="text-[#A39171]" />
            <span className="text-sm">Personalised for <strong>{company}</strong></span>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mt-10">
          <Card kind="attending" title="We're Attending" sub="LinkedIn · 1200×630" />
          <Card kind="meet-us" title="Meet Us at the Summit" sub="LinkedIn · 1200×630" />
          <Card kind="logo" title="Event Logo" sub="Transparent · 1200×630" />
        </div>

        <div className="mt-16 grid lg:grid-cols-2 gap-8">
          <div>
            <h2 className="text-2xl mb-4">Post Templates</h2>
            <div className="grid gap-4">
              {POSTS.map((p) => (
                <div key={p.id} className="card-flat p-6" data-testid={`post-${p.id}`}>
                  <div className="overline mb-3">{p.label}</div>
                  <p className="text-sm leading-relaxed text-zinc-700 whitespace-pre-line">{p.body(company)}</p>
                  <div className="mt-2 text-xs text-[#A39171]">{HASHTAGS.join(" ")}</div>
                  <button onClick={() => copy(`${p.body(company)}\n\n${HASHTAGS.join(" ")}`)}
                    className="btn-outline-black mt-4 h-10 text-xs" data-testid={`copy-post-${p.id}`}>
                    <Copy size={14} /> Copy Caption
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-2xl mb-4">Hashtags</h2>
            <div className="card-flat p-6">
              <div className="flex flex-wrap gap-2">
                {HASHTAGS.map((h) => <span key={h} className="pill">{h}</span>)}
              </div>
              <button onClick={() => copy(HASHTAGS.join(" "))} className="btn-outline-black mt-5 h-10 text-xs">
                <Copy size={14} /> Copy All
              </button>
            </div>
            <h2 className="text-2xl mt-10 mb-4">Share Summit Hub</h2>
            <div className="card-flat p-6 flex items-center gap-4">
              <img src={`${BACKEND_URL}/api/qr?data=${encodeURIComponent(window.location.origin)}`} alt="QR" className="h-32 w-32" />
              <div>
                <div className="overline mb-2">QR Code</div>
                <p className="text-sm text-zinc-600">Print on collateral or share on slides.</p>
                <a href={`${BACKEND_URL}/api/qr?data=${encodeURIComponent(window.location.origin)}`} download="summit-qr.png" className="btn-outline-black mt-4 h-10 text-xs">
                  <Download size={14} /> Download
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function SocialGraphic({ kind, company }) {
  const headline = kind === "attending" ? "WE'RE ATTENDING" : kind === "meet-us" ? "MEET US AT" : "STRATEGIC SUPPLIER SUMMIT";
  const subline = kind === "logo" ? "Travel + Leisure × Display America" : "Travel + Leisure Strategic Supplier Summit";
  return (
    <svg id={`graphic-${kind}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 630" className="w-full block">
      <rect width="1200" height="630" fill="#0A0A0A" />
      <line x1="60" y1="60" x2="1140" y2="60" stroke="#A39171" strokeWidth="2" />
      <line x1="60" y1="570" x2="1140" y2="570" stroke="#A39171" strokeWidth="2" />
      <text x="60" y="120" fontFamily="Manrope" fill="#A39171" fontSize="22" letterSpacing="6">MAY 12–13, 2026 · NEW YORK</text>
      <text x="60" y="260" fontFamily="Playfair Display" fill="#fff" fontSize="84" fontWeight="500">{headline}</text>
      <text x="60" y="360" fontFamily="Playfair Display" fill="#fff" fontSize="64">{company}</text>
      <text x="60" y="500" fontFamily="Manrope" fill="#bbb" fontSize="22">{subline}</text>
      <text x="60" y="540" fontFamily="Manrope" fill="#A39171" fontSize="18" letterSpacing="4">PRODUCED BY DISPLAY AMERICA</text>
    </svg>
  );
}

async function downloadSvgAsPng(svgId, filename) {
  const svg = document.getElementById(svgId);
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 1200; canvas.height = 630;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((pngBlob) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(pngBlob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };
  img.src = url;
}
